import type { ToolCallBox, ToolCallLinkEntry } from "@demo/server/api/contracts/toolCall";
import { strings } from "@demo/utils/strings";

import ImageTileStrip from "./ImageTileStrip";
import ToolCallInputImage from "./ToolCallInputImage";
import { describeResultCount } from "./ToolCallResultsBody";

export interface ImageSearchResultsListProps {
  /** The image that was searched, resolved from the run's own media by `asset_idx`; absent when it cannot be shown. */
  inputImageSrc?: string;
  /** The part of the image that was searched, when the model searched a region rather than the whole image. */
  region?: ToolCallBox;
  results: ToolCallLinkEntry[];
  /** While the search runs the strip shows generic placeholder tiles, not thumbnails. */
  isStreaming: boolean;
}

/**
 * The expanded body of `search.image_search`, the reverse image search (not to be confused with
 * `search.text_image_search`, a text query that returns images). An Input section previews the
 * image that was searched, then a status line and the tile strip of matches.
 */
function ImageSearchResultsList({ inputImageSrc, region, results, isStreaming }: ImageSearchResultsListProps) {
  const copy = strings.toolCall;
  const status = isStreaming ? copy.imageResults.searching : describeResultCount(results.length);

  return (
    <div className="flex w-full flex-col gap-5 px-2 pb-2 pt-4">
      <div className="flex w-full flex-col gap-2">
        <span className="whitespace-nowrap text-foreground-disabled label-2">{copy.visit.inputLabel}</span>
        <ToolCallInputImage
          alt={copy.imageResults.inputAlt}
          region={region}
          src={inputImageSrc}
          testId="image-search-input"
        />
      </div>
      <div className="flex w-full flex-col gap-2">
        <span className="whitespace-nowrap text-foreground-disabled label-2" data-testid="image-search-status">
          {status}
        </span>
        <ImageTileStrip isStreaming={isStreaming} results={results} />
      </div>
    </div>
  );
}

export default ImageSearchResultsList;
